
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Send, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from '@/context/AuthContext';

interface SendEmailFormProps {
  emailContent: string;
  userEmail?: string;
  isMobile?: boolean;
  onEmailSent?: () => void;
  onCancel?: () => void;
  isInlineForm?: boolean;
}

const SendEmailForm = ({ 
  emailContent, 
  userEmail, 
  isMobile = false,
  onEmailSent,
  onCancel,
  isInlineForm = false 
}: SendEmailFormProps) => {
  const [recipient, setRecipient] = useState<string>("");
  const [subject, setSubject] = useState<string>("Neues Versicherungsangebot für Sie");
  const [ccEmail, setCcEmail] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSendEmail = async () => {
    if (!recipient || !subject) {
      toast({
        title: "Fehlende Information",
        description: "Bitte geben Sie einen Empfänger und einen Betreff ein.",
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const ccEmails = ccEmail.split(',').map(email => email.trim()).filter(Boolean);
      
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: recipient,
          subject: subject,
          html: emailContent,
          userId: user?.id,
          cc: ccEmails.length > 0 ? ccEmails : undefined,
          replyTo: userEmail || undefined
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: "E-Mail gesendet",
        description: "Die E-Mail wurde erfolgreich versendet.",
      });
      
      // Call the onEmailSent callback if it exists
      if (onEmailSent) {
        onEmailSent();
      }
      
      // Reset form
      setRecipient("");
      setSubject("");
      setCcEmail("");
      setMessage("");
      
    } catch (error: any) {
      console.error('Error sending email:', error);
      setError(error.message || 'Unbekannter Fehler beim Senden der E-Mail');
      toast({
        title: "Fehler beim Senden",
        description: `Die E-Mail konnte nicht gesendet werden: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={`space-y-4 ${isInlineForm ? 'bg-gray-800/30 p-3 rounded-md border border-gray-700' : ''}`}>
      {isInlineForm && (
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-medium text-white">E-Mail senden</h3>
          <Button 
            variant="ghost" 
            size="sm"
            className="p-1 h-auto text-gray-400 hover:text-white"
            onClick={onCancel}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {error && (
        <div className="bg-red-900/30 border border-red-800/50 text-red-300 p-3 rounded-md text-sm">
          <p className="font-medium">Fehler beim Senden:</p>
          <p>{error}</p>
        </div>
      )}
      
      <div className="space-y-2">
        <Label htmlFor="recipient" className={isInlineForm ? "text-white" : ""}>Empfänger*in</Label>
        <Input
          id="recipient"
          type="email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="email@beispiel.de"
          className={isInlineForm ? "bg-gray-800 border-gray-700 text-white" : ""}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="subject" className={isInlineForm ? "text-white" : ""}>Betreff</Label>
        <Input
          id="subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Betreff der E-Mail"
          className={isInlineForm ? "bg-gray-800 border-gray-700 text-white" : ""}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="cc" className={isInlineForm ? "text-white" : ""}>CC (Optional)</Label>
        <Input
          id="cc"
          value={ccEmail}
          onChange={(e) => setCcEmail(e.target.value)}
          placeholder="cc1@beispiel.de, cc2@beispiel.de"
          className={isInlineForm ? "bg-gray-800 border-gray-700 text-white" : ""}
        />
        <p className="text-xs text-gray-500">Mehrere E-Mail-Adressen durch Kommas trennen</p>
      </div>
      
      {!isInlineForm && userEmail && (
        <div className="rounded-md bg-gray-100 p-3 border border-gray-200">
          <p className="text-sm text-gray-600">
            <strong>Von:</strong> {userEmail || "Ihr E-Mail-Konto"} (Antworten werden an diese Adresse gesendet)
          </p>
        </div>
      )}
      
      <div className="flex gap-2">
        {onCancel && (
          <Button 
            variant={isInlineForm ? "outline" : "secondary"} 
            onClick={onCancel}
            className={isInlineForm ? "border-gray-700 text-gray-200 hover:bg-gray-800 flex-1" : "flex-1"}
          >
            Abbrechen
          </Button>
        )}
        <Button 
          onClick={handleSendEmail} 
          disabled={isSending || !recipient || !subject}
          className={`${isInlineForm ? "bg-jade hover:bg-jade-light text-white" : ""} flex-1`}
        >
          {isSending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Senden...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Senden
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SendEmailForm;
